import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface RequestItem {
  id: string;
  inventory_item_id: string;
  item_name: string;
  item_code: string;
  unit: string;
  requested_qty: number;
  fulfilled_qty: number;
  status: string;
  on_hand: number;
}

interface ConfirmIssueDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  requestId: string | null;
  roomName: string;
  userId: string;
  /** When true, expose the manager override to issue beyond on-hand. */
  isManager?: boolean;
  onIssued?: () => void;
}

const ConfirmIssueDialog: React.FC<ConfirmIssueDialogProps> = ({
  open, onOpenChange, requestId, roomName, userId, isManager = false, onIssued,
}) => {
  const { toast } = useToast();
  const [items, setItems] = useState<RequestItem[]>([]);
  const [issueQtys, setIssueQtys] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [allowNegative, setAllowNegative] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  // Stable idempotency key per issue attempt; reused on retry, rotated on success.
  const idempotencyKeyRef = useRef<string>('');

  useEffect(() => {
    if (open) {
      idempotencyKeyRef.current =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : '';
    }
  }, [open]);

  useEffect(() => {
    if (!open || !requestId) return;
    setLoading(true);
    setAllowNegative(false);
    setOverrideReason('');
    (async () => {
      const { data } = await supabase
        .from('inventory_room_request_items')
        .select('id, inventory_item_id, requested_qty, fulfilled_qty, status, inventory_items!inner(name, given_id, unit, warehouse_on_hand)')
        .eq('room_request_id', requestId) as any;

      const mapped: RequestItem[] = (data || []).map((r: any) => ({
        id: r.id,
        inventory_item_id: r.inventory_item_id,
        item_name: r.inventory_items?.name || '',
        item_code: r.inventory_items?.given_id || '',
        unit: r.inventory_items?.unit || '',
        requested_qty: r.requested_qty,
        fulfilled_qty: r.fulfilled_qty || 0,
        status: r.status,
        on_hand: Number(r.inventory_items?.warehouse_on_hand ?? 0),
      }));
      setItems(mapped);
      const defaults: Record<string, string> = {};
      mapped.forEach((item) => {
        const remaining = item.requested_qty - item.fulfilled_qty;
        defaults[item.id] = remaining > 0 ? String(remaining) : '0';
      });
      setIssueQtys(defaults);
      setLoading(false);
    })();
  }, [open, requestId]);

  const handleConfirm = async () => {
    if (!requestId) return;
    setSubmitting(true);

    const lines = items
      .filter((item) => Number(issueQtys[item.id] || 0) > 0)
      .map((item) => ({
        request_item_id: item.id,
        issue_qty: Number(issueQtys[item.id]),
      }));

    if (lines.length === 0) {
      toast({ title: 'No items to issue', description: 'Enter quantities first.', variant: 'destructive' });
      setSubmitting(false);
      return;
    }

    // Negative-stock guard: any line issuing more than on-hand needs the override.
    const shortLines = items.filter(
      (item) => Number(issueQtys[item.id] || 0) > item.on_hand
    );
    if (shortLines.length > 0 && !allowNegative) {
      toast({
        title: 'Not enough stock',
        description: `${shortLines[0].item_name}: only ${shortLines[0].on_hand} available. ${
          isManager ? 'Enable manager override to issue anyway.' : 'Reduce the quantity or ask a manager.'
        }`,
        variant: 'destructive',
      });
      setSubmitting(false);
      return;
    }
    if (shortLines.length > 0 && allowNegative && overrideReason.trim() === '') {
      toast({ title: 'Reason required', description: 'Enter a reason for the override.', variant: 'destructive' });
      setSubmitting(false);
      return;
    }

    const { data, error } = await supabase.rpc('confirm_room_request_issue', {
      p_request_id: requestId,
      p_issued_by: userId,
      p_lines: lines,
      ...(idempotencyKeyRef.current ? { p_idempotency_key: idempotencyKeyRef.current } : {}),
      p_allow_negative: shortLines.length > 0 && allowNegative,
      p_override_reason: shortLines.length > 0 && allowNegative ? overrideReason.trim() : null,
    }) as any;

    if (error) {
      toast({ title: 'Issue failed', description: error.message, variant: 'destructive' });
    } else {
      const result = Array.isArray(data) ? data[0] : data;
      toast({
        title: 'Issue confirmed',
        description: `${result?.total_issued || 0} units issued. Request status: ${result?.new_request_status || 'updated'}.`,
      });
      idempotencyKeyRef.current =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : '';
      onIssued?.();
      onOpenChange(false);
    }
    setSubmitting(false);
  };

  // Fills every line's issue qty with its planned (outstanding) quantity —
  // the common "everything was picked as planned" path.
  const handleIssueAllAsPlanned = () => {
    setIssueQtys(() => {
      const next: Record<string, string> = {};
      items.forEach((item) => {
        const remaining = item.requested_qty - item.fulfilled_qty;
        next[item.id] = remaining > 0 ? String(remaining) : '0';
      });
      return next;
    });
  };

  const totalToIssue = items.reduce((sum, item) => sum + Number(issueQtys[item.id] || 0), 0);
  const hasShortage = items.some((item) => Number(issueQtys[item.id] || 0) > item.on_hand);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Confirm Issue — {roomName}</DialogTitle>
          <DialogDescription>Enter the quantity to issue for each item. Stock will be deducted from warehouse.</DialogDescription>
        </DialogHeader>

        {loading ? (
          <p className="text-sm text-slate-500 py-8 text-center">Loading items...</p>
        ) : (
          <>
          <div className="flex justify-end">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleIssueAllAsPlanned}
              disabled={items.every((item) => item.requested_qty - item.fulfilled_qty <= 0)}
            >
              Issue all as planned
            </Button>
          </div>
          <div className="overflow-hidden rounded-xl border border-slate-200">
            <Table>
              <TableHeader className="bg-slate-50">
                <TableRow>
                  <TableHead>Item</TableHead>
                  <TableHead className="text-right">Available</TableHead>
                  <TableHead className="text-right">Requested</TableHead>
                  <TableHead className="text-right">Already issued</TableHead>
                  <TableHead className="text-right">Issue now</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => {
                  const remaining = item.requested_qty - item.fulfilled_qty;
                  const issuing = Number(issueQtys[item.id] || 0);
                  const isShort = issuing > item.on_hand;
                  return (
                    <TableRow key={item.id}>
                      <TableCell>
                        <p className="font-medium text-slate-900">{item.item_name}</p>
                        <p className="text-xs text-slate-500">{item.item_code} · {item.unit}</p>
                        {isShort ? (
                          <p className="text-xs font-medium text-red-600">
                            Only {item.on_hand} available, you are issuing {issuing} — change qty or manager override.
                          </p>
                        ) : null}
                      </TableCell>
                      <TableCell className={`text-right tabular-nums ${isShort ? 'text-red-600 font-medium' : ''}`}>
                        {item.on_hand}
                      </TableCell>
                      <TableCell className="text-right">{item.requested_qty}</TableCell>
                      <TableCell className="text-right">
                        {item.fulfilled_qty > 0 ? (
                          <Badge variant="outline" className="border-green-300 text-green-700">{item.fulfilled_qty}</Badge>
                        ) : '0'}
                      </TableCell>
                      <TableCell className="text-right">
                        {remaining > 0 ? (
                          <Input
                            type="number"
                            min={0}
                            max={remaining}
                            value={issueQtys[item.id] || ''}
                            onChange={(e) => setIssueQtys((prev) => ({ ...prev, [item.id]: e.target.value }))}
                            className="w-20 text-right ml-auto"
                          />
                        ) : (
                          <Badge variant="outline" className="border-green-300 text-green-700">Done</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {hasShortage ? (
            isManager ? (
              <div className="rounded-xl border border-red-200 bg-red-50 p-3 space-y-2">
                <label className="flex items-center gap-2 text-sm font-medium text-red-800">
                  <input
                    type="checkbox"
                    checked={allowNegative}
                    onChange={(e) => setAllowNegative(e.target.checked)}
                  />
                  Manager override — issue beyond available stock (drives on-hand negative)
                </label>
                {allowNegative ? (
                  <Input
                    placeholder="Reason for override (required, logged to audit trail)"
                    value={overrideReason}
                    onChange={(e) => setOverrideReason(e.target.value)}
                  />
                ) : null}
              </div>
            ) : (
              <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                One or more lines exceed available stock. Reduce the quantity, or ask an
                admin / operations manager to issue with an override.
              </div>
            )
          ) : null}
          </>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
          <Button
            onClick={handleConfirm}
            disabled={
              submitting ||
              totalToIssue === 0 ||
              (hasShortage && (!isManager || !allowNegative))
            }
          >
            {submitting ? 'Issuing...' : `Confirm issue (${totalToIssue} units)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ConfirmIssueDialog;
